import config from 'config'
import { getAppVersionLabel } from 'helpers/appVersion'
import { getOrCreatePosDeviceId } from 'utils/posDeviceId'

const DIAGNOSTIC_ENDPOINT = '/client-diagnostics/batch'
const BASE_RETRY_MS = 15_000
const MAX_RETRY_MS = 15 * 60_000
const PERIODIC_FLUSH_MS = 60_000

let started = false
let flushing = false
let retryDelayMs = BASE_RETRY_MS
let retryTimer: number | null = null
let intervalTimer: number | null = null

function readAuth(): {
  token: string
  tenantSlug: string | null
  branchId: string | null
} | null {
  const raw = localStorage.getItem('authUser')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as {
      token?: string
      tenant_slug?: string
      user?: { current_branch_id?: string | null }
    }
    if (!parsed.token) return null
    return {
      token: parsed.token,
      tenantSlug: localStorage.getItem('tenant_slug') || parsed.tenant_slug || null,
      branchId: localStorage.getItem('selected_branch_id') || parsed.user?.current_branch_id || null
    }
  } catch {
    return null
  }
}

function currentRoute(): string | null {
  if (typeof window === 'undefined') return null
  return `${window.location.pathname}${window.location.hash}`.split('?')[0].slice(0, 500)
}

function safeMessage(value: unknown): string {
  if (value instanceof Error) return value.message || value.name
  if (typeof value === 'string') return value
  return 'Unexpected client error'
}

function isUnimportantDiagnostic(
  event: Pick<ClientDiagnosticInput, 'category' | 'code' | 'metadata'>
): boolean {
  if (event.code === 'network_request_failed') return true
  if (event.category === 'network' && /^http_4\d\d$/.test(event.code)) return true
  return (
    event.code === 'checkout_persistence_lost' &&
    event.metadata?.phase === 'after_append'
  )
}

export async function recordClientDiagnostic(
  input: ClientDiagnosticInput
): Promise<ClientDiagnosticEvent | null> {
  try {
    if (isUnimportantDiagnostic(input)) return null
    if (!window.electronAPI?.recordDiagnostic) return null
    const auth = readAuth()
    const recorded = await window.electronAPI.recordDiagnostic({
      source: 'renderer',
      severity: input.severity || 'error',
      ...input,
      device_id: input.device_id || getOrCreatePosDeviceId(),
      branch_id: input.branch_id || auth?.branchId || null,
      client_version: input.client_version || getAppVersionLabel().replace(/^v/, ''),
      route: input.route || currentRoute(),
      online: input.online ?? navigator.onLine
    })
    if (recorded?.support_code && recorded.severity === 'critical') {
      window.dispatchEvent(
        new CustomEvent('diagnostics:support-code', {
          detail: { supportCode: recorded.support_code }
        })
      )
    }
    return recorded
  } catch {
    // Diagnostics must never affect financial or operational flows.
    return null
  }
}

export async function recordClientError(
  code: string,
  error: unknown,
  overrides: Partial<ClientDiagnosticInput> = {}
): Promise<ClientDiagnosticEvent | null> {
  const err = error instanceof Error ? error : null
  return recordClientDiagnostic({
    source: 'renderer',
    severity: 'error',
    category: 'crash',
    code,
    message: safeMessage(error),
    stack: err?.stack || null,
    ...overrides
  })
}

function scheduleRetry(): void {
  if (retryTimer) window.clearTimeout(retryTimer)
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    void flushClientDiagnostics()
  }, retryDelayMs)
  retryDelayMs = Math.min(MAX_RETRY_MS, retryDelayMs * 2)
}

export async function flushClientDiagnostics(): Promise<void> {
  if (flushing || !navigator.onLine || !window.electronAPI?.readDiagnosticBatch) return
  const auth = readAuth()
  if (!auth?.token || !auth.branchId) return

  flushing = true
  try {
    const localEvents = await window.electronAPI.readDiagnosticBatch(100)
    if (!localEvents.length) {
      retryDelayMs = BASE_RETRY_MS
      return
    }

    const ignoredIds = localEvents
      .filter(isUnimportantDiagnostic)
      .map((event) => event.event_id)
    if (ignoredIds.length) await window.electronAPI.ackDiagnostics(ignoredIds)
    const events = localEvents.filter((event) => !isUnimportantDiagnostic(event))
    if (!events.length) {
      retryDelayMs = BASE_RETRY_MS
      return
    }

    const enriched = events.map((event) => ({
      ...event,
      device_id: event.device_id || getOrCreatePosDeviceId(),
      branch_id: auth.branchId,
      client_version: event.client_version || getAppVersionLabel().replace(/^v/, '')
    }))
    const response = await fetch(`${config.api.API_URL}${DIAGNOSTIC_ENDPOINT}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
        'X-Branch-Id': auth.branchId,
        'X-Device-Id': getOrCreatePosDeviceId(),
        'X-Client-Version': getAppVersionLabel().replace(/^v/, ''),
        ...(auth.tenantSlug ? { 'X-Tenant-Slug': auth.tenantSlug } : {})
      },
      body: JSON.stringify({ events: enriched })
    })
    if (!response.ok) throw new Error(`diagnostic_upload_${response.status}`)
    const payload = (await response.json()) as { acknowledged_event_ids?: string[] }
    const acknowledged = Array.isArray(payload.acknowledged_event_ids)
      ? payload.acknowledged_event_ids
      : enriched.map((event) => event.event_id)
    await window.electronAPI.ackDiagnostics(acknowledged)
    retryDelayMs = BASE_RETRY_MS
    if (localEvents.length === 100) window.setTimeout(() => void flushClientDiagnostics(), 250)
  } catch {
    // Deliberately do not record failures of the diagnostics endpoint: that
    // would create a recursive error loop and grow the local queue forever.
    scheduleRetry()
  } finally {
    flushing = false
  }
}

export async function exportClientDiagnostics(): Promise<{ canceled: boolean; filePath?: string }> {
  try {
    if (!window.electronAPI?.exportDiagnostics) return { canceled: true }
    return await window.electronAPI.exportDiagnostics()
  } catch {
    return { canceled: true }
  }
}

export function startClientDiagnostics(): void {
  if (started || typeof window === 'undefined') return
  started = true

  window.addEventListener('error', (event) => {
    void recordClientError('renderer_window_error', event.error || event.message, {
      severity: 'critical',
      category: event.filename?.includes('/static/') ? 'update' : 'crash',
      metadata: { phase: 'window_error' }
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    void recordClientError('renderer_unhandled_rejection', event.reason, {
      severity: 'critical',
      category: 'crash',
      metadata: { phase: 'unhandled_rejection' }
    })
  })

  const triggerFlush = () => void flushClientDiagnostics()
  const showSupportCode = (event: Event) => {
    const supportCode = (event as CustomEvent<{ supportCode?: string }>).detail?.supportCode
    if (!supportCode) return
    void import('helpers/WarningNotification')
      .then(({ default: showWarning }) => {
        void import('i18next').then(({ default: i18n }) => {
          showWarning(`${i18n.t('common.unexpectedError.supportCode')}: ${supportCode}`)
        })
      })
      .catch(() => {})
  }
  window.addEventListener('online', triggerFlush)
  window.addEventListener('auth:updated', triggerFlush)
  window.addEventListener('branch:changed', triggerFlush)
  window.addEventListener('diagnostics:support-code', showSupportCode)
  window.setTimeout(triggerFlush, 5000)
  intervalTimer = window.setInterval(triggerFlush, PERIODIC_FLUSH_MS)
}

export function stopClientDiagnosticsForTests(): void {
  if (retryTimer) window.clearTimeout(retryTimer)
  if (intervalTimer) window.clearInterval(intervalTimer)
  retryTimer = null
  intervalTimer = null
  retryDelayMs = BASE_RETRY_MS
  flushing = false
  started = false
}
